// Shimmer skeleton for the eDOS Test Detail page. Mirrors the real
// layout (back link, crumbs, hero block, four-card info grid, ICD-10
// chips, branch availability table, related tests + sticky pricing
// aside) so when the API resolves the swap from skeleton to content is
// visually smooth — no layout jump, no flash of empty state.
//
// Uses the same colour tokens as the live page (MP.bg / MP.border)
// and a shared pulse keyframe rather than the cream/parchment Shimmer
// primitive so it visually fits the portal's teal/sage system.
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { MP } from '../medical/shared';

// Single shimmering ghost block. width/height in CSS values, rounded
// in px (defaults to a small 4px corner so it reads as text).
const Ghost = ({ width = '100%', height = 12, rounded = 4, sx }) => (
  <Box
    sx={{
      width,
      height,
      borderRadius: `${rounded}px`,
      bgcolor: MP.border,
      flexShrink: 0,
      animation: 'edos-detail-pulse 1.4s ease-in-out infinite',
      '@keyframes edos-detail-pulse': {
        '0%, 100%': { opacity: 0.45 },
        '50%':      { opacity: 0.85 },
      },
      ...sx,
    }}
  />
);

// One info card in the four-card grid. Header bar + 5 InfoRow ghosts.
const InfoCardGhost = () => (
  <Box
    sx={{
      bgcolor: MP.white,
      border: `1px solid ${MP.border}`,
      borderRadius: '6px',
      boxShadow: MP.shadow,
      p: 2.5,
    }}
  >
    <Ghost width={120} height={11} sx={{ mb: 2 }} />
    {[0, 1, 2, 3, 4].map((i) => (
      <Stack
        key={i}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 1.25, borderBottom: i === 4 ? 'none' : `1px solid ${MP.border}` }}
      >
        <Ghost width={'40%'} height={11} />
        <Ghost width={'35%'} height={11} />
      </Stack>
    ))}
  </Box>
);

const EdosTestDetailShimmer = () => (
  <Stack spacing={2.5}>
    {/* Back link */}
    <Ghost width={120} height={14} />

    {/* Crumbs — five tiny segments */}
    <Stack direction="row" spacing={1} alignItems="center">
      <Ghost width={36} height={9} />
      <Ghost width={6} height={9} rounded={2} />
      <Ghost width={120} height={9} />
      <Ghost width={6} height={9} rounded={2} />
      <Ghost width={90} height={9} />
      <Ghost width={6} height={9} rounded={2} />
      <Ghost width={160} height={9} />
    </Stack>

    {/* Hero block — code pill row + huge name + aliases line + meta pills */}
    <Box
      sx={{
        bgcolor: MP.white,
        border: `1px solid ${MP.border}`,
        borderRadius: '6px',
        boxShadow: MP.shadow,
        p: { xs: 3, md: 4 },
      }}
    >
      <Ghost width={140} height={11} sx={{ mb: 2 }} />
      <Ghost width={'70%'} height={32} sx={{ mb: 1.5 }} />
      <Ghost width={'45%'} height={14} sx={{ mb: 2.5 }} />
      <Stack direction="row" spacing={1.25}>
        <Ghost width={130} height={26} rounded={3} />
        <Ghost width={150} height={26} rounded={3} />
        <Ghost width={120} height={26} rounded={3} />
      </Stack>
    </Box>

    {/* Two-column grid: 4 info cards in main, sticky pricing aside */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 380px' },
        gap: 2.5,
        alignItems: 'start',
      }}
    >
      {/* Main column */}
      <Stack spacing={2.5}>
        {/* Four info cards in 2×2 grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2,
          }}
        >
          <InfoCardGhost />
          <InfoCardGhost />
          <InfoCardGhost />
          <InfoCardGhost />
        </Box>

        {/* Additional information block — purple-headed paragraphs */}
        <Box
          sx={{
            bgcolor: MP.white,
            border: `1px solid ${MP.border}`,
            borderRadius: '6px',
            boxShadow: MP.shadow,
            p: 2.5,
          }}
        >
          <Ghost width={180} height={12} sx={{ mb: 2 }} />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2.5 }}>
            {[60, 80, 70, 90, 65, 75, 85, 70].map((w, i) => (
              <Ghost key={i} width={w} height={20} rounded={999} />
            ))}
          </Stack>
          <Ghost width={'95%'} height={11} sx={{ mb: 1 }} />
          <Ghost width={'88%'} height={11} sx={{ mb: 1 }} />
          <Ghost width={'92%'} height={11} sx={{ mb: 1 }} />
          <Ghost width={'76%'} height={11} />
        </Box>

        {/* ICD-10 chips */}
        <Box
          sx={{
            bgcolor: MP.white,
            border: `1px solid ${MP.border}`,
            borderRadius: '6px',
            boxShadow: MP.shadow,
            p: 2.5,
          }}
        >
          <Ghost width={140} height={12} sx={{ mb: 2 }} />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {[180, 200, 160, 220, 170, 190].map((w, i) => (
              <Ghost key={i} width={w} height={22} rounded={999} />
            ))}
          </Stack>
        </Box>

        {/* Branch availability table — header + 6 rows */}
        <Box
          sx={{
            bgcolor: MP.white,
            border: `1px solid ${MP.border}`,
            borderRadius: '6px',
            boxShadow: MP.shadow,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Ghost width={180} height={12} />
          </Box>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Stack
              key={i}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: `1px solid ${MP.border}`,
              }}
            >
              <Ghost width={'30%'} height={11} />
              <Ghost width={'20%'} height={11} />
              <Ghost width={80} height={20} rounded={999} />
              <Ghost width={'15%'} height={11} />
            </Stack>
          ))}
        </Box>

        {/* Related tests — 4 stub rows */}
        <Box>
          <Ghost width={180} height={12} sx={{ mb: 1.5 }} />
          <Stack spacing={1}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  bgcolor: MP.white,
                  border: `1px solid ${MP.border}`,
                  borderRadius: '6px',
                  boxShadow: MP.shadow,
                  px: 2,
                  py: 1.5,
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr 80px 90px',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <Ghost width={'80%'} height={11} />
                <Ghost width={'70%'} height={12} />
                <Ghost width={40} height={11} />
                <Ghost width={50} height={14} sx={{ marginLeft: 'auto' }} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>

      {/* Sticky pricing aside */}
      <Box
        sx={{
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: '6px',
          boxShadow: MP.shadow,
          p: 2.5,
          position: { lg: 'sticky' },
          top: { lg: 84 },
          alignSelf: 'flex-start',
        }}
      >
        <Ghost width={100} height={10} sx={{ mb: 1.5 }} />
        <Ghost width={'60%'} height={36} sx={{ mb: 0.75 }} />
        <Ghost width={'40%'} height={11} sx={{ mb: 2.5 }} />

        {/* 3 stat rows */}
        {[0, 1, 2].map((i) => (
          <Stack
            key={i}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 1.25, borderTop: `1px solid ${MP.border}` }}
          >
            <Ghost width={'45%'} height={10} />
            <Ghost width={'30%'} height={11} />
          </Stack>
        ))}

        {/* CTA buttons */}
        <Stack spacing={1.25} sx={{ mt: 2.5 }}>
          <Ghost width={'100%'} height={36} rounded={3} />
          <Ghost width={'100%'} height={36} rounded={3} />
          <Stack direction="row" spacing={1}>
            <Ghost width={'100%'} height={32} rounded={3} />
            <Ghost width={'100%'} height={32} rounded={3} />
          </Stack>
        </Stack>

        {/* Sample report card */}
        <Box
          sx={{
            mt: 2.5,
            p: 2,
            border: `1px solid ${MP.border}`,
            borderRadius: '6px',
            bgcolor: MP.bg,
          }}
        >
          <Ghost width={120} height={10} sx={{ mb: 1.25 }} />
          <Ghost width={'90%'} height={11} sx={{ mb: 0.5 }} />
          <Ghost width={'70%'} height={11} />
        </Box>
      </Box>
    </Box>
  </Stack>
);

export default EdosTestDetailShimmer;
